import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type InsertIllustration =
  Database['public']['Tables']['product_image']['Insert']
export type Illustration_ = Database['public']['Tables']['product_image']['Row']

export const useIllustrationsApi = ({
  illustration_id,
}: {
  illustration_id?: number
}) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { toast } = useToast()

  const { data: illustrations } = useQuery({
    queryKey: [queryKeys.illustrations],
    queryFn: async () =>
      await supabase
        .from('product_image')
        .select('*')
        .eq('id', illustration_id!),
    enabled: !!illustration_id,
  })

  const createIllustration = useMutation({
    mutationFn: async (insertIllustration: InsertIllustration) =>
      await supabase
        .from('product_image')
        .insert(insertIllustration)
        .select('id')
        .single(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.illustrations],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.packageProducts],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariants],
      })
    },
  })

  const updateIllustration = useMutation({
    mutationFn: async (insertIllustration: InsertIllustration) =>
      await supabase
        .from('product_image')
        .update(insertIllustration)
        .eq('id', insertIllustration.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.illustrations] })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.packageProducts],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariants],
      })

      toast({
        title: 'Illustration updated',
      })
    },
  })

  const updateIllustrationsBulk = useMutation({
    mutationFn: async (rows: InsertIllustration[]) =>
      await supabase.from('product_image').upsert(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.illustrations] })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.packageProducts],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariants],
      })

      toast({
        title: 'Illustrations updated',
      })
    },
    onError: () => {
      toast({
        title: 'Failed to update illustrations',
        variant: 'destructive',
      })
    },
  })

  const deleteIllustration = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('product_image').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.illustrations] })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.packageProducts],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariants],
      })
    },
  })

  return {
    illustrations,
    createIllustration,
    updateIllustration,
    updateIllustrationsBulk,
    deleteIllustration,
  }
}
